import { useCallback } from 'react';
import type {
  Nullable,
  ConfigEntityDetail,
  EnvironmentKey,
  EntityStatus,
  EntityStatusFilter,
  EntityStatusParam,
} from '@web-config-app/core';
import { useConfigEntityListQuery } from '../use-config-entity-list-query/use-config-entity-list-query';
import { useConfigEntityGetByIdQuery } from '../use-config-entity-get-by-id-query/use-config-entity-get-by-id-query';
import {
  batchAsyncOperations,
  isRetryableError,
} from '../../utilities/batch-async-operations/batch-async-operations';

export interface GetAllEntitiesResult {
  /** Successfully fetched entities */
  entities: ConfigEntityDetail[];
  /** Number of entities that failed to fetch */
  failedCount: number;
  /** Errors from failed entity fetches */
  errors: unknown[];
}

export interface GetAllEntitiesProgressHandlers {
  /**
   * Called after each list page returns with the IDs found on that page.
   * Fires incrementally so callers can show progress while IDs are still
   * being gathered, before the final total is known.
   */
  onPageFetched?: (params: {
    pageEntityIds: string[];
    cumulativeCount: number;
  }) => void | Promise<void>;
  onEntityIdsResolved?: (params: {
    totalCount: number;
    entityIds: string[];
  }) => void | Promise<void>;
  onEntityFetched?: (params: {
    entityId: string;
    entityName?: string;
    entity: ConfigEntityDetail;
  }) => void | Promise<void>;
  onEntityFetchFailed?: (params: {
    entityId: string;
    entityName?: string;
    error: unknown;
  }) => void | Promise<void>;
  onEntityRetrying?: (params: {
    entityId: string;
    entityName?: string;
  }) => void | Promise<void>;
}

/**
 * Error thrown when fetching all entities fails after retries. Contains the errors for display.
 */
export interface GetAllEntitiesFailureError extends Error {
  /** All errors from failed fetches */
  errors: unknown[];
  /** Number of entities that failed */
  failedCount: number;
}

/**
 * Creates an error for when fetching all entities fails, with the failed errors attached
 */
export const createGetAllEntitiesFailureError = (
  failedCount: number,
  errors: unknown[],
): GetAllEntitiesFailureError => {
  const error = new Error(
    'GetAllEntitiesFailureError',
  ) as GetAllEntitiesFailureError;
  error.errors = errors;
  error.failedCount = failedCount;
  return error;
};

/* Type guard to check if an error is a GetAllEntitiesFailureError.
 * We check for the specific message marker to distinguish from ConfigApiError,
 * which also has an 'errors' array but represents a different error type.
 */
export const isGetAllEntitiesFailureError = (
  error: unknown,
): error is GetAllEntitiesFailureError =>
  error != null &&
  typeof error === 'object' &&
  'errors' in error &&
  Array.isArray((error as GetAllEntitiesFailureError).errors) &&
  (error as GetAllEntitiesFailureError).message ===
    'GetAllEntitiesFailureError';

/**
 * A hook that provides functionality to retrieve all entities of a specific type by paginating through API results.
 *
 * This hook first fetches entity IDs from the list endpoint, then makes individual GET requests for each entity
 * to ensure the latest data from capability is retrieved. The GET requests do not include references.
 *
 * The hook will make up to 100 list API calls to gather entity IDs, then fetch each entity individually.
 * This means the maximum number of entities that can be fetched is 100 × pageSize (e.g., 10,000 entities
 * with the default pageSize of 100).
 *
 * For individual entity fetches, retryable errors (429, 5xx, network errors) are automatically retried
 * up to 3 times with a 5 second delay between attempts to account for potential rate limiting or server errors for large batches of entities. If the error is not retryable, it will be thrown.
 *
 * If some entities fail to fetch, the operation throws unless the caller passes options.allowPartialResults,
 * in which case the successfully fetched entities are returned along with a count of failures.
 *
 * @param params - Configuration parameters for fetching entities
 * @param params.environment - The environment configuration to query entities from
 * @param params.domainEntityType - Optional domain entity type to filter results by specific entity type
 * @param params.status - Optional entity status to filter results by specific status
 * @param params.searchQuery - Optional search query string to filter results
 * @param params.callInterval - Time in milliseconds to wait between API calls (default: 200ms)
 * @param params.pageSize - Number of entities to fetch per page (default: 100)
 *
 * @returns A callback function that returns { entities, failedCount }
 *
 * @example
 * ```tsx
 * const getAllEntities = useGetAllEntitiesOfType({
 *   environment: myEnvironment,
 *   domainEntityType: 'config-activity',
 *   status: 'published',
 * });
 *
 * const { entities, failedCount } = await getAllEntities();
 * console.log(`Fetched ${entities.length}, failed ${failedCount}`);
 * ```
 */

/** Max concurrent individual entity GET requests */
export const MAX_CONCURRENCY_FOR_GET_ENTITY_BY_ID = 20;
const MAX_PAGES = 200;
/** Max retry attempts for retryable failures (429, 5xx, network errors) */
export const MAX_RETRIES = 3;
/** Delay in ms between retry attempts */
export const RETRY_DELAY_MS = 5000;
/** Default time in ms to wait between API calls */
export const DEFAULT_CALL_INTERVAL = 200;

export const useGetAllEntitiesOfType = ({
  environment,
  domainEntityType,
  status,
  searchQuery = null,
  callInterval = DEFAULT_CALL_INTERVAL,
  pageSize = 100,
}: {
  environment: EnvironmentKey;
  domainEntityType: string;
  status?: EntityStatus;
  searchQuery?: Nullable<string>;
  callInterval?: number;
  pageSize?: number;
}) => {
  const getEntities = useConfigEntityListQuery({
    environment,
    domainEntityType,
  });
  const getEntityById = useConfigEntityGetByIdQuery({
    sourceEnvironment: environment,
    includeReferences: false,
    domainEntityType,
  });

  const getAllEntitiesOfType = useCallback(
    async (
      handlers?: GetAllEntitiesProgressHandlers,
      abortSignal?: AbortSignal,
      options?: {
        /** Override the hook-level status filter at call time. Supports comma-separated values (e.g. 'draft,published'). */
        status?: EntityStatusFilter;
        /** When true, return partial results instead of throwing on fetch failures. */
        allowPartialResults?: boolean;
      },
    ) => {
      // Step 1: Gather all entity IDs from the list endpoint
      const allEntityIds: string[] = [];
      const entityNamesById = new Map<string, string | undefined>();
      const effectiveStatus = options?.status ?? status;

      for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex += 1) {
        if (abortSignal?.aborted) break;
        const absoluteOffset = pageIndex * pageSize;
        const params = {
          'page[offset]': String(absoluteOffset),
          'page[size]': String(pageSize),
          ...(domainEntityType
            ? { 'filter[domainEntityType]': domainEntityType }
            : {}),
          ...(searchQuery
            ? { 'filter[query]': encodeURIComponent(searchQuery) }
            : {}),
          ...(effectiveStatus
            ? {
                'filter[status]': (Array.isArray(effectiveStatus)
                  ? effectiveStatus.join(',')
                  : effectiveStatus) as EntityStatusParam,
              }
            : {}),
        };

        // eslint-disable-next-line no-await-in-loop
        const page = await getEntities(params);
        const items = Array.isArray(page) ? page : [];

        // If no items returned, we've reached the end
        if (!items.length) {
          break;
        }

        items.forEach((item) => {
          entityNamesById.set(item.id, item.attributes?.entityMetadata?.name);
        });

        const pageEntityIds = items.map((item) => item.id);
        allEntityIds.push(...pageEntityIds);

        // eslint-disable-next-line no-await-in-loop
        await handlers?.onPageFetched?.({
          pageEntityIds,
          cumulativeCount: allEntityIds.length,
        });

        // If we got fewer items than pageSize, this is the last page
        if (items.length < pageSize) {
          break;
        }

        // provide a wait period between requests
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, callInterval);
        });
      }

      if (abortSignal?.aborted) {
        throw new DOMException('Operation was cancelled', 'AbortError');
      }

      await handlers?.onEntityIdsResolved?.({
        totalCount: allEntityIds.length,
        entityIds: allEntityIds,
      });

      // Step 2: Fetch each entity individually via GET to get the latest data from capability
      // Continue on error with smart retry for retryable errors (429, 5xx, network errors)
      const {
        results: fullySyncedEntities,
        failedCount,
        errors,
      } = await batchAsyncOperations({
        items: allEntityIds,
        operation: async (entityId) => {
          const response = await getEntityById(entityId);
          if (response?.data) {
            await handlers?.onEntityFetched?.({
              entityId,
              entityName: entityNamesById.get(entityId),
              entity: response.data,
            });
          }
          return response;
        },
        concurrency: MAX_CONCURRENCY_FOR_GET_ENTITY_BY_ID,
        rateLimit: { intervalMs: callInterval, intervalCap: 1 },
        continueOnError: true,
        maxRetries: MAX_RETRIES,
        retryDelayMs: RETRY_DELAY_MS,
        shouldRetry: isRetryableError,
        onItemFailed: async ({ item, error }) => {
          await handlers?.onEntityFetchFailed?.({
            entityId: item,
            entityName: entityNamesById.get(item),
            error,
          });
        },
        onItemRetrying: async ({ item }) => {
          await handlers?.onEntityRetrying?.({
            entityId: item,
            entityName: entityNamesById.get(item),
          });
        },
        abortSignal,
      });

      if (abortSignal?.aborted) {
        throw new DOMException('Operation was cancelled', 'AbortError');
      }

      // If any entities still failed after retries, throw unless the caller
      // opted into partial results.
      if (failedCount > 0 && !options?.allowPartialResults) {
        throw createGetAllEntitiesFailureError(failedCount, errors);
      }

      const entities = fullySyncedEntities
        .map((response) => response?.data)
        .filter((data): data is ConfigEntityDetail => data != null);

      return { entities, failedCount, errors };
    },
    [
      pageSize,
      getEntities,
      getEntityById,
      domainEntityType,
      searchQuery,
      callInterval,
      status,
    ],
  );

  return getAllEntitiesOfType;
};
